import React from 'react';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { Link } from 'react-router-dom';
import { compose, withProps, withState, withStateHandlers, lifecycle } from 'recompose';
import { Modal as _Modal, Steps, Icon, notification } from 'antd';
import { omit, fill, set, cloneDeep, difference } from 'lodash';
import styled, { css } from 'styled-components';

import { actions as productActions } from 'modules/product';
import { actions as authActions } from 'modules/auth';
import { WithConfirmOrangeButton, BlueButton, DefaultButton } from 'components/Core/Buttons';
import Loader from 'components/Core/Loader';
import ProductPricing from './ProductPricing';
import BookForm from 'components/Form/BookForm';
import TravelerInformationForm from 'components/Form/TravelerInformationForm';
import HotelDeliveryForm from 'components/Form/HotelDeliveryForm';
import TicketQuestionForm from 'components/Form/TicketQuestionForm';
import BookSummaryForm from 'components/Form/BookSummaryForm';
import {
  getChildPriceObj,
  getHotelObj,
  isMultiTier,
  isRequireDeliveryLocation,
  isMatchPassportName,
  getDistinctQuestions,
  isRequireDate,
  mergeMeetingPoints,
  isRequireMeetingPoint
} from 'utils/product';
import { isRequireHotelName } from 'utils/booking';
import config from 'config';

moment.tz.setDefault('Asia/Bangkok');

const Modal = styled(_Modal)`
  .ant-modal-body {
    position: relative;
  }
`;

const FullLoader = styled(Loader)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3;
  background-color: rgba(255,255,255, 0.8);
`;

const StyleSteps = styled(Steps)`
  font-family: Montserrat !important;
  margin-bottom: 15px !important;
  @media(max-width: 768px) {
    margin-bottom: 10px !important;
  }
`;

const Title = styled.p`
  font-family: Montserrat !important;
  width: 450px;
  font-weight: 600;
  @media(max-width: 768px) {
    width: 300px;
  }
`;

const actionButton = css`
  width: 120px;
`;
const BuyButton = styled(WithConfirmOrangeButton)`
  ${actionButton}
`;

const NextButton = styled(BlueButton)`
  ${actionButton}
`;

const BackButton = styled(DefaultButton)`
  ${actionButton}
`;

const HR = styled.hr`
  border-color: ${props => props.theme.lightGray2};
  border-style: solid;
  margin-bottom: 12px;
`;

const InfoIcon = styled(Icon)`
  font-size: 16px;
  color: ${props => props.theme.blue};
`;

const getSelectedTier = (multiTier = [], currentTier) => multiTier.find(t => t.tier === currentTier);

const getPricingBody = (product, bookObj) => {
  const selectedOptions = [];
  if (getChildPriceObj(product.additional_options)) {
    selectedOptions.push({ ...getChildPriceObj(product.additional_options), quantity: bookObj.childQuantity });
  }
  if (getHotelObj(product.additional_options)) {
    selectedOptions.push({ ...getHotelObj(product.additional_options), quantity: bookObj.hotelQuantity });
  }
  return omit({
    ...bookObj,
    selected_options: selectedOptions,
    multi_tier_quantity: isMultiTier(product) ?
      product.multi_tier_prices.map(tier => ({
        ...tier,
        sub_tiers: tier.sub_tiers.map((subTier, index) => ({
          ...subTier,
          quantity: tier.tier === bookObj.selectedTier ? bookObj.subTier[index] : 0,
          ...tier.tier === bookObj.selectedTier && Object.keys(bookObj.questions).length > 0 ? {
            globaltix_questions: subTier.globaltix_questions.map(
              q => {
                if (q.id && bookObj.questions[q.id]) {
                  return {
                    ...q,
                    answer: bookObj.questions[q.id]
                  };
                }
                if (q.type === 'DATE') {
                  return {
                    ...q,
                    answer: bookObj.date.date.format('YYYY-MM-DD')
                  };
                }
                return q;
              }
            )
          } : {}
        }))
      })) :
      []
  }, ['childQuantity', 'selectedTier', 'subTier', 'hotelQuantity']);
};

const shouldValidate = (step, product, bookObj) => {
  if (step === 1) {
    const dateValidation = isRequireDate(product) ? !!bookObj.date : true;
    if (isMultiTier(product)) {
      return !!(
        !!bookObj.selectedTier &&
        !!bookObj.subTier &&
        dateValidation &&
        bookObj.subTier.filter(st => st > 0).length > 0
      );
    }
    return dateValidation;
  }
  const meetingPointValidation = isRequireMeetingPoint(product) ? !!bookObj.user.meetingPoint : true;
  const hotelNameValidation = isRequireHotelName(bookObj) ? !!bookObj.user.hotelName : true;
  const validateUserInfo = !!(
    !!bookObj.user.firstName &&
    !!bookObj.user.lastName &&
    !!bookObj.user.email &&
    !!bookObj.user.phone &&
    !!bookObj.user.country &&
    meetingPointValidation &&
    hotelNameValidation
  );
  const validateDelivery = !!(
    !!bookObj.delivery.name &&
    !!bookObj.delivery.room
  );
  if (isMultiTier(product)) {
    const answeredQuestion = Object.keys(bookObj.questions).filter(id => bookObj.questions[id]);
    const toAnswerQuestion = getDistinctQuestions(
      getSelectedTier(product.multi_tier_prices, bookObj.selectedTier)
    )
      .filter(q => q.type !== 'DATE')
      .map(q => q.id.toString());
    if (difference(toAnswerQuestion, answeredQuestion).length === 0) {
      return validateUserInfo;
    }
    return false;
  }
  if (!isRequireDeliveryLocation(product)) return validateUserInfo;
  else if (isMatchPassportName(product)) return validateUserInfo && validateDelivery && bookObj.user.nameTitle;
  return validateUserInfo && validateDelivery;
};

const initialBookState = {
  book: { quantity: 1, childQuantity: 0, user: {}, delivery: {}, questions: {}, hotelQuantity: 0 }
};

const enhance = compose(
  connect(
    state => ({
      product: state.product.selectedProduct,
      productAvailability: state.product.productAvailability,
      isLoadingAvailability: state.product.isLoadingAvailability,
      isShowBookModal: state.product.isShowBookModal,
      price: state.product.price,
      stations: state.app.stations,
      isLoadingPrice: state.product.isLoadingPrice,
      isBookingProduct: state.product.isBookingProduct,
      isLoadingProduct: state.product.isLoadingProduct,
      user: state.auth.user,
    }),
    { ...productActions, ...authActions, push }
  ),
  withProps(
    props => ({
      getPrice: data => props.getPrice(data)
    })
  ),
  withState('step', 'setStep', 1),
  withState('monthYear', 'setMonthYear', moment().format('MMMM YYYY')),
  withStateHandlers(
    initialBookState,
    {
      setField: ({ book }, { product, getPrice, monthYear, loadAvailability }) => (field, value) => {
        const bookObj = set(cloneDeep(book), field, value);
        let selectedTier;
        if (field === 'selectedTier') {
          selectedTier = getSelectedTier(product.multi_tier_prices, value);
          bookObj.subTier = fill(Array(selectedTier.sub_tiers.length), 0);
        }
        const childPrice = getChildPriceObj(product.additional_options);
        if (childPrice && bookObj.childQuantity + bookObj.quantity > product.max_travelers) {
          bookObj.childQuantity = product.max_travelers - bookObj.quantity;
        }
        if (isMultiTier(product)) {
          selectedTier = getSelectedTier(product.multi_tier_prices, bookObj.selectedTier);
        }
        if (!(
          field === 'date' ||
          field === 'questions' ||
          field.indexOf('user') !== -1 ||
          field.indexOf('delivery') !== -1
        )) {
          getPrice(getPricingBody(product, bookObj));
        }

        if (selectedTier) {
          const ticketIds = selectedTier.sub_tiers
            .filter(subTier => subTier.has_availability)
            .map(subTier => subTier.globaltix_ticket_id);

          let tripId = product._id;
          if (ticketIds.length > 0) {
            tripId += `&ticket_ids=${ticketIds}`;
          }
          if (book.selectedTier !== bookObj.selectedTier) {
            loadAvailability(tripId, monthYear);
            return { book: omit(bookObj, 'date') };
          }
        }
        return { book: bookObj };
      },
      resetState: () => () => initialBookState
    }
  ),
  withProps(
    props => ({
      closeModal: () => {
        props.setMonthYear(moment().format('MMMM YYYY'));
        props.closeModal();
        props.setStep(1);
      },
      onChangeMonthYear: monthYear => {
        props.setMonthYear(monthYear);
        const selectedTier = props.product.multi_tier_prices.find(tier => tier.tier === props.book.selectedTier);
        if (!selectedTier) {
          return props.loadAvailability(props.product._id, monthYear);
        }
        const ticketIds = selectedTier.sub_tiers
          .filter(subTier => subTier.has_availability)
          .map(subTier => subTier.globaltix_ticket_id);

        let tripId = props.product._id;
        if (ticketIds.length > 0) {
          tripId += `&ticket_ids=${ticketIds}`;
        }
        return props.loadAvailability(tripId, monthYear);
      },
      onBook: async () => {
        const { book, product } = props;
        let priceObj = getPricingBody(product, book);
        const omitFields = ['user', 'date', 'delivery', 'nameTitle'];
        if (priceObj.multi_tier_quantity.length > 0) {
          omitFields.push('quantity');
        } else {
          omitFields.push('multi_tier_quantity');
        }
        if (priceObj.selected_options.length === 0) {
          omitFields.push('selected_options');
        }
        let meetingPoint;
        if (isRequireDeliveryLocation(product)) {
          meetingPoint = `Hotel name: ${priceObj.delivery.name}, room: ${priceObj.delivery.room}`;
        } else if (isRequireMeetingPoint(product)) {
          meetingPoint = book.user.meetingPoint;
          if (isRequireHotelName(book)) meetingPoint += `, ${book.user.hotelName}`;
        }
        if (meetingPoint) {
          priceObj.meeting_point = meetingPoint;
        }
        priceObj = {
          ...omit(priceObj, omitFields),
          name: {
            first: priceObj.user.firstName,
            last: priceObj.user.lastName
          },
          email: priceObj.user.email,
          ...isRequireDate(product) ? {
            trip_date: priceObj.date.date.toISOString()
          } : {},
          mobile: priceObj.user.phone,
          country: priceObj.user.country,
          ...isMatchPassportName(product) ? { nameTitle: priceObj.user.name_title } : {}
        };
        const res = await props.bookProduct(priceObj);
        if (res && res.transaction_id) {
          props.closeModal();
          if (!res.success) {
            notification.error({
              message: res.message,
              description: 'Please see more detail in transaction page.',
              duration: 10
            });
          } else {
            notification.success({
              message: 'Book successful'
            });
          }
          await props.getMe();
          props.push(`/transaction/${res.transaction_id}`);
        }
      }
    })
  ),
  lifecycle({
    componentWillReceiveProps(nextProps) {
      if (nextProps.product && nextProps.product._id && this.props.product._id !== nextProps.product._id) {
        this.props.resetState();
        this.props.setField('trip_id', nextProps.product._id);
      }
    }
  })
);

const ProductModal = props => {
  const {
    product,
    book,
    isShowBookModal,
    isBookingProduct,
    isLoadingProduct,
    productAvailability,
    isLoadingAvailability,
    stations,
    price,
    step,
    monthYear,
    isLoadingPrice,
    user
  } = props;
  const validate = product ? shouldValidate(step, product, book) : false;
  const selectedTier = product && isMultiTier(product) ? getSelectedTier(product.multi_tier_prices, book.selectedTier) : null;
  const isInsufficientCredit = (user.partner_credit || 0) < price.grandTotal;
  const isShowProductPricing = ((product && product.multi_tier_prices && product.multi_tier_prices.length > 0) ?
    book.selectedTier :
    (book.quantity > 0 || book.childQuantity > 0)
  );
  const isShowCalendar = (product && product.multi_tier_prices && product.multi_tier_prices.length <= 0) || book.selectedTier;
  const meetingPoints = (product && product.meeting_points) ? mergeMeetingPoints(product.meeting_points, stations) : [];
  return (
    <Modal
      width={600}
      maskClosable={false}
      visible={isShowBookModal}
      footer={isInsufficientCredit ? null : [
        step > 1 ? <BackButton icon="left" onClick={() => props.setStep(step - 1)}>Previous</BackButton> : null,
        step !== 3 ? <NextButton disabled={!validate} onClick={() => props.setStep(step + 1)}>Next <Icon type="right" /></NextButton> : null,
        step === 3 ?
          <BuyButton disabled={!validate} icon="shopping-cart" loading={isBookingProduct} onClick={props.onBook}>Book</BuyButton> : null
      ]}
      onCancel={props.closeModal}
      title={product && (
        <Title>{product.name} <Link target="_blank" to={`${config.webPath}/trip/${product.slug}`}><InfoIcon type="info-circle" /></Link></Title>
      )}
    >
      {isBookingProduct && !isLoadingProduct && <FullLoader text="Processing..." />}
      {isLoadingProduct && <Loader />}
      {!isLoadingProduct && (
        <div>
          <StyleSteps size="small" current={step - 1}>
            <Steps.Step title="Date & Quantity" />
            <Steps.Step title="Information" />
            <Steps.Step title="Summary" />
          </StyleSteps>
          {step === 1 && [
            (isShowProductPricing && price.grandTotal > 0) ?
              <ProductPricing
                loading={isLoadingPrice}
                price={price}
                product={product}
                credit={user.partner_credit || 0}
                book={book}
              /> : null,
            <BookForm
              showCalendar={isShowCalendar}
              book={book}
              product={product}
              currentDate={monthYear}
              onChange={props.setField}
              onChangeMonthYear={props.onChangeMonthYear}
              tripAvailabilities={productAvailability}
              loading={isLoadingAvailability}
            />
          ]}
          {step === 2 && [
            <TravelerInformationForm onChange={props.setField} book={book} product={product} meetingPoints={meetingPoints} />,
            isRequireDeliveryLocation(product) ? [
              <HR />,
              <HotelDeliveryForm onChange={props.setField} book={book} />
            ] : null,
            selectedTier ? [
              <HR />,
              <TicketQuestionForm
                selectedTier={getSelectedTier(product.multi_tier_prices, book.selectedTier)}
                onChange={props.setField}
                book={book}
              />
            ] : null
          ]}
          {step === 3 && <BookSummaryForm book={book} product={product} totalPrice={price.grandTotal} />}
        </div>
      )}
    </Modal>
  );
};

export default enhance(ProductModal);



// WEBPACK FOOTER //
// ./src/components/Product/ProductBookModal.js